import { DataModelAttributeOption } from '@/types/DataModel';

/**
 * Shared option-list utilities for every React-side consumer of
 * `DataModelAttributeOption[]`. Each helper here is a parallel implementation
 * of an AngularJS algorithm that lives in `dataModel.js`. The legacy editor
 * (AngularJS) and the new editor (React) are mutually exclusive at the user
 * level — a customer is either fully on one or fully on the other — so both
 * implementations are alive in the codebase, but a single user only ever
 * exercises one of them. Drift between the two would manifest as different
 * behavior for two customers on the same data model.
 *
 * Each function below names its AngularJS counterpart in a `DRIFT-PARTNER`
 * note. If you change one side, change the other.
 *
 * When `dataModel.js` is eventually retired (full migration to the React
 * editor), the AngularJS-side copies and these `DRIFT-PARTNER` notes can be
 * deleted and the React version becomes the single source of truth.
 */

/**
 * Normalize one server-shaped option to the `{ key, value, … }` form expected
 * by `DataModelAttributeOption` and React consumers.
 *
 * DRIFT-PARTNER — `web/main/src/main/webapp/app/dataModel.js#fetchAndCacheOptionListAsync`,
 * specifically the `_.forEach(response, function(option) { optionList.options.push({...}) })`
 * block (~lines 1558-1570). That block performs the equivalent normalization
 * for old-editor users and uses an explicit 9-field whitelist
 * (`name, serviceName, key, value, label, labels, icon, icons, groups`).
 * If you add a new field, server-derived translation, or fallback rule here,
 * apply the same change there. See also the AngularJS-shape mapper in
 * `services/angularjs-bridge.ts#hydrateAngularOptionListFromOptions`, which
 * shares the same field whitelist on the React→AngularJS direction.
 */
export const normalizeOptionListOption = (
    option: DataModelAttributeOption,
): DataModelAttributeOption => ({
    ...option,
    key: option.key ?? option.name ?? '',
    value: option.value ?? option.label ?? '',
});

/** Normalize a server-shaped option list (see {@link normalizeOptionListOption}). */
export const normalizeOptionListOptions = (
    options: DataModelAttributeOption[],
): DataModelAttributeOption[] => options.map(normalizeOptionListOption);

/**
 * Filters an option list by group, supporting both the legacy form
 * (`optionListGroups[groupName] = [optionKey, ...]`) and the modern
 * per-option `option.groups` form. Pre-filters out options with no group
 * association when `groupNames` is non-empty, matching the legacy behavior.
 *
 * DRIFT-PARTNER — `web/main/src/main/webapp/app/dataModel.js#getGroupFilteredOptions`
 * (~lines 395-426). Same algorithm, same priority order
 * (optionListGroups > option.groups > exclude). Verified equivalent for the
 * realistic input space; the only divergence is in unreachable edge cases
 * (e.g. `optionListGroups` accidentally an array). If you fix a bug or change
 * a priority rule in either place, mirror it in the other.
 */
export const filterOptionListOptionsByGroups = (
    options: DataModelAttributeOption[],
    optionListGroups: Record<string, string[]> | undefined,
    groupNames: string[] | undefined,
): DataModelAttributeOption[] => {
    if (options.length === 0 || !groupNames?.length) {
        return options;
    }
    const hasOptionListGroups =
        optionListGroups && Object.keys(optionListGroups).length > 0;
    return options.filter((option) => {
        if (hasOptionListGroups) {
            return Object.entries(optionListGroups).some(
                ([groupName, groupOptions]) =>
                    groupNames.includes(groupName) &&
                    groupOptions.includes(option.key),
            );
        }
        if (option.groups?.length) {
            return option.groups.some((g) => groupNames.includes(g));
        }
        return false;
    });
};
